import React, { useState, useMemo } from 'react';
import { Input, Button, Alert, Modal, Table } from 'antd';
import { SendOutlined, RobotOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { queryTrafficAnalysis } from './APIs/fcdApi';
import { useLoading } from '../../common/loading/LoadingProvider';
import { AiIcon } from '../../constants/icons';
import '../../styles/AIAssistant.css';

const { TextArea } = Input;

/**
 * AI Assistant Component for traffic analysis
 * Allows users to ask questions about traffic data and highlights relevant roads on the map
 */
function Interpreter({ onHighlightRoads, onZoomToRoad, onClose, geoData }) {
  const [query, setQuery] = useState('');
  const { showLoader, hideLoader } = useLoading();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showStreetList, setShowStreetList] = useState(false);

  // Get street details from geoData based on UnifiedIDs
  const streetDetails = useMemo(() => {
    if (!result?.unifiedIds || !geoData?.features) return [];

    return result.unifiedIds.map(unifiedId => {
      const feature = geoData.features.find(f => {
        const hash = f.properties.hash || f.properties.UnifiedID;
        return hash === unifiedId;
      });

      if (feature) {
        const props = feature.properties;
        const records = props.allRecords || [];

        // Calculate average speed and flow from all records
        let avgSpeed = 0;
        let avgFlow = 0;
        let avgTravelTime = 0;

        if (records.length > 0) {
          const speeds = records.filter(r => r.AvgSp).map(r => parseFloat(r.AvgSp));
          const flows = records.filter(r => r.Flusso_Espanso).map(r => parseFloat(r.Flusso_Espanso));
          const travelTimes = records.filter(r => r.AvgTt).map(r => parseFloat(r.AvgTt));

          if (speeds.length > 0) avgSpeed = speeds.reduce((a, b) => a + b, 0) / speeds.length;
          if (flows.length > 0) avgFlow = flows.reduce((a, b) => a + b, 0) / flows.length;
          if (travelTimes.length > 0) avgTravelTime = travelTimes.reduce((a, b) => a + b, 0) / travelTimes.length;
        }

        return {
          key: unifiedId,
          unifiedId: unifiedId,
          streetName: props.StreetName || 'N/A',
          length: (props.Length ? props.Length * 1000 : (props.len_sum_m || 0)),
          speedLimit: props.SpeedLimit || 'N/A',
          avgSpeed: avgSpeed.toFixed(1),
          avgFlow: avgFlow.toFixed(0),
          avgTravelTime: avgTravelTime.toFixed(1),
          frc: props.FRC || 'N/A'
        };
      }

      return {
        key: unifiedId,
        unifiedId: unifiedId,
        streetName: 'Unknown',
        length: 0,
        speedLimit: 'N/A',
        avgSpeed: '0',
        avgFlow: '0',
        avgTravelTime: '0',
        frc: 'N/A'
      };
    });
  }, [result, geoData]);

  const columns = [
    {
      title: 'Nome Strada',
      dataIndex: 'streetName',
      key: 'streetName',
      ellipsis: true,
      width: 180,
    },
    {
      title: 'Velocità Media (km/h)',
      dataIndex: 'avgSpeed',
      key: 'avgSpeed',
      width: 120,
      align: 'center',
      sorter: (a, b) => parseFloat(a.avgSpeed) - parseFloat(b.avgSpeed),
    },
    {
      title: 'Flusso Medio (VEH)',
      dataIndex: 'avgFlow',
      key: 'avgFlow',
      width: 140,
      align: 'center',
      sorter: (a, b) => parseFloat(a.avgFlow) - parseFloat(b.avgFlow),
    },
    {
      title: 'Tempo Percorrenza (s)',
      dataIndex: 'avgTravelTime',
      key: 'avgTravelTime',
      width: 130,
      align: 'center',
      sorter: (a, b) => parseFloat(a.avgTravelTime) - parseFloat(b.avgTravelTime),
    },
    {
      title: 'Lunghezza (m)',
      dataIndex: 'length',
      key: 'length',
      width: 100,
      align: 'center',
      render: (val) => typeof val === 'number' ? val.toFixed(0) : val,
    },
  ];

  const handleSubmit = async () => {
    if (!query.trim()) return;

    setLoading(true);
    showLoader("Analisi in corso...");
    setError(null);
    setResult(null);

    try {
      const data = await queryTrafficAnalysis(query);

      if (data.success) {
        const rawResultIds = data.result || [];
        const uniqueResultIds = Array.isArray(rawResultIds)
          ? Array.from(new Set(rawResultIds))
          : (rawResultIds ? [rawResultIds] : []);
        const count = uniqueResultIds.length;

        setResult({
          explanation: data.explanation,
          interpretation: data.interpretation,
          metrics: data.metrics,
          unifiedIds: uniqueResultIds,
          resultCount: count
        });

        // Highlight the roads on the map
        if (uniqueResultIds.length > 0 && onHighlightRoads) {
          onHighlightRoads(uniqueResultIds);
        }
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('AI Analysis error:', err);
      setError(err.message || 'Si è verificato un errore durante l\'analisi');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleReset = () => {
    setQuery('');
    setResult(null);
    setError(null);
    setShowStreetList(false);
    if (onHighlightRoads) {
      onHighlightRoads([]);
    }
  };

  return (
    <div className="ai-assistant-wrapper">
      <div className="ai-assistant-header">
        <img src={AiIcon} alt="AI" className="ai-assistant-icon" />
        <h3>Interpreter</h3>
      </div>

      <div className="ai-assistant-content">
        <p className="ai-assistant-description">
          Fai una domanda sui dati del traffico e l'AI evidenzierà le strade rilevanti sulla mappa.
        </p>

        {!result ? (
          <>
            <TextArea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Es: Quali sono le strade più congestionate? Dove ci sono stati i peggiori rallentamenti?"
              autoSize={{ minRows: 3, maxRows: 6 }}
              disabled={loading}
              className="ai-assistant-input"
            />

            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSubmit}
              disabled={!query.trim() || loading}
              className="ai-assistant-submit"
              block
            >
              {loading ? 'Analisi in corso...' : 'Analizza'}
            </Button>
          </>
        ) : (
          <div className="ai-assistant-result">
            <div className="ai-result-query">
              <strong>La tua domanda:</strong>
              <p>{query}</p>
            </div>

            <div className="ai-result-explanation">
              <RobotOutlined className="ai-result-icon" />
              <div>
                <strong>Analisi:</strong>
                <p>{result.explanation}</p>
              </div>
            </div>

            {result.interpretation && (
              <div className="ai-result-interpretation">
                <div>
                  <strong>Interpretazione:</strong>
                  <p>{result.interpretation}</p>
                </div>
              </div>
            )}

            {result.metrics && (
              <div className="ai-result-metrics">
                <strong>Metriche:</strong>
                <div className="ai-metrics-grid">
                  {result.metrics.average_speed_selected_links_kmh !== null && result.metrics.average_speed_selected_links_kmh !== undefined && (
                    <div className="ai-metric-card">
                      <span className="ai-metric-value">{result.metrics.average_speed_selected_links_kmh}</span>
                      <span className="ai-metric-label">Velocità Media Selezionate (km/h)</span>
                    </div>
                  )}
                  {result.metrics.overall_average_speed_kmh !== null && result.metrics.overall_average_speed_kmh !== undefined && (
                    <div className="ai-metric-card">
                      <span className="ai-metric-value">{result.metrics.overall_average_speed_kmh}</span>
                      <span className="ai-metric-label">Velocità Media Generale (km/h)</span>
                    </div>
                  )}
                  {result.metrics.number_of_selected_links !== null && result.metrics.number_of_selected_links !== undefined && (
                    <div className="ai-metric-card">
                      <span className="ai-metric-value">{Math.round(result.metrics.number_of_selected_links)}</span>
                      <span className="ai-metric-label">Link Selezionati</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="ai-result-stats">
              <span className="ai-result-count">
                {result.resultCount} {result.resultCount === 1 ? 'strada trovata' : 'strade trovate'}
              </span>
              <span className="ai-result-highlight-note">
                Le strade sono evidenziate sulla mappa
              </span>
            </div>

            {result.resultCount > 0 && (
              <Button
                icon={<UnorderedListOutlined />}
                onClick={() => setShowStreetList(true)}
                className="ai-assistant-list-btn"
                block
              >
                Visualizza lista strade
              </Button>
            )}

            <Button
              onClick={handleReset}
              className="ai-assistant-reset"
              block
            >
              Nuova domanda
            </Button>
          </div>
        )}

        {error && (
          <Alert
            message="Sempre al tuo servizio!"
            description="Saremmo entusiasti di fare molto di più, ma il periodo demo di Traffix è terminato. Contattaci per sbloccare l'intero potenziale!"
            type="warning"
            showIcon
            closable
            onClose={() => setError(null)}
            className="ai-assistant-error"
          />
        )}
      </div>

      {/* Street List Modal */}
      <Modal
        title={
          <div className="ai-modal-header">
            <UnorderedListOutlined />
            <span>Strade Trovate ({result?.resultCount || 0})</span>
          </div>
        }
        open={showStreetList}
        onCancel={() => setShowStreetList(false)}
        footer={[
          <Button key="close" onClick={() => setShowStreetList(false)}>
            Chiudi
          </Button>
        ]}
        width={800}
        className="ai-street-list-modal"
        centered
      >
        <div className="ai-modal-query">
          <strong>Domanda:</strong> {query}
        </div>
        <Table
          columns={columns}
          dataSource={streetDetails}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `Totale: ${total} strade` }}
          scroll={{ x: 700 }}
          size="small"
          className="ai-street-table"
          onRow={(record) => ({
            onMouseEnter: () => {
              if (onHighlightRoads) onHighlightRoads([record.unifiedId]);
            },
            onMouseLeave: () => {
              if (onHighlightRoads && result?.unifiedIds) onHighlightRoads(result.unifiedIds);
            },
            onClick: () => {
              if (onZoomToRoad && geoData?.features) {
                const feature = geoData.features.find(f => {
                  const hash = f.properties.hash || f.properties.UnifiedID;
                  return hash === record.unifiedId;
                });
                if (feature) onZoomToRoad([feature]);
              }
            }
          })}
        />
      </Modal>
    </div>
  );
}

export default Interpreter;
